/**
 * Granular cookie preferences (RQ78378 / RQ78379).
 *
 * Per-category toggles. Strictly necessary is always-on and non-toggleable.
 * Only categories that have ≥1 registered tracker are shown (today: Analytics).
 * Default toggle state is jurisdiction-aware: opt-in (OFF) under consent_required,
 * default-on under opt_out. GPC, when active, forces non-essential off + disables
 * the toggle (cannot be overridden).
 */
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createDebug } from '../../utils/debugLog'
import Modal from '../ui/Modal'
import { useConsent } from '../../consent/useConsent'
import {
  type ConsentCategories,
  type ConsentCategory,
  defaultCategories,
} from '../../consent/consentTypes'
import { categoriesWithTrackers, TRACKERS } from '../../consent/trackers'

const log = createDebug('dw:cookie-consent:preferences-modal')

type ToggleableCategory = 'functional' | 'analytics' | 'marketing'

const TOGGLEABLE_CATEGORIES: ToggleableCategory[] = ['functional', 'analytics', 'marketing']

/** i18n keys for the trackers in a category; the caller resolves them via `t()`. */
function trackerLabelKeysForCategory(category: ConsentCategory): string[] {
  return TRACKERS.filter((tracker) => tracker.category === category).map(
    (tracker) => tracker.labelKey
  )
}

interface ConsentPreferencesModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function ConsentPreferencesModal({
  isOpen,
  onClose,
}: ConsentPreferencesModalProps) {
  const { t } = useTranslation('settings')
  const { consent, jurisdiction, gpc, savePreferences } = useConsent()

  // Which non-necessary categories to show (those that have a tracker today).
  const shown = useMemo<ToggleableCategory[]>(() => {
    const withTrackers = categoriesWithTrackers()
    return TOGGLEABLE_CATEGORIES.filter((c) => withTrackers.has(c))
  }, [])

  // Initialize toggle state from stored consent, else jurisdiction default.
  const initialCats = useMemo<ConsentCategories>(() => {
    if (consent) return consent.cats
    // opt_out and not_required default non-essential ON (so unticking is an
    // explicit opt-out); consent_required is opt-in (OFF).
    if (jurisdiction === 'opt_out' || jurisdiction === 'not_required') {
      return { analytics: true, functional: true, marketing: true }
    }
    return defaultCategories()
  }, [consent, jurisdiction])

  const [cats, setCats] = useState<ConsentCategories>(initialCats)

  useEffect(() => {
    if (isOpen) {
      setCats(initialCats)
      log('open jurisdiction=%s gpc=%s', jurisdiction, gpc)
    }
  }, [isOpen, initialCats, jurisdiction, gpc])

  const toggle = (category: ToggleableCategory) => {
    // GPC categories cannot be enabled. (analytics/marketing are GPC-governed.)
    if (gpc && category !== 'functional') return
    setCats((prev) => ({ ...prev, [category]: !prev[category] }))
  }

  const handleSave = () => {
    // Enforce GPC at save: non-essential GPC categories cannot be turned on.
    const finalCats: ConsentCategories = gpc
      ? { ...cats, analytics: false, marketing: false }
      : cats
    log('save analytics=%s functional=%s marketing=%s', finalCats.analytics, finalCats.functional, finalCats.marketing)
    savePreferences(finalCats)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" title={t('consentPreferences.title')}>
      <div className="flex flex-col gap-4" data-testid="consent-preferences-modal">
        <p className="text-sm text-muted-foreground">
          {t('consentPreferences.description')}
        </p>

        {gpc && (
          <div
            className="text-xs font-medium rounded-md bg-surface-2 px-3 py-2 text-muted-foreground"
            data-testid="consent-gpc-note"
          >
            {t('consentPreferences.gpcNote')}
          </div>
        )}

        {/* Strictly necessary — always on, non-toggleable. */}
        <div className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">{t('consentPreferences.strictlyNecessary')}</span>
            <span className="text-xs font-medium text-muted-foreground">
              {t('consentPreferences.strictlyNecessaryDescription')}
            </span>
          </div>
          <span
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            data-testid="consent-toggle-strictly_necessary"
          >
            {t('consentPreferences.alwaysOn')}
          </span>
        </div>

        {shown.map((category) => {
          const label = t(`consentPreferences.${category}`)
          const description = t(`consentPreferences.${category}Description`)
          const disabled = gpc && category !== 'functional'
          const checked = disabled ? false : cats[category]
          const vendors = trackerLabelKeysForCategory(category).map((key) => t(key))
          return (
            <div
              key={category}
              className="flex items-start justify-between gap-4 rounded-md border border-border px-3 py-3"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">{label}</span>
                <span className="text-xs font-medium text-muted-foreground">{description}</span>
                {vendors.length > 0 && (
                  <span className="text-xs font-medium text-muted-foreground/80 mt-1">
                    {vendors.join(', ')}
                  </span>
                )}
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(category)}
                  data-testid={`consent-toggle-${category}`}
                  aria-label={t('consentPreferences.cookiesAriaLabel', { label })}
                />
              </label>
            </div>
          )
        })}

        <div className="flex justify-end gap-2 mt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm font-medium bg-transparent hover:bg-surface-2 text-muted-foreground transition-colors cursor-pointer"
            data-testid="consent-prefs-cancel"
          >
            {t('consentPreferences.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-md text-sm font-medium bg-primary hover:opacity-90 text-primary-foreground transition-colors cursor-pointer"
            data-testid="consent-prefs-save"
          >
            {t('consentPreferences.savePreferences')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
